import { useMemo, useState } from 'react';
import { Button, Flex, Text } from '@mantine/core';
import { BasicMantineTable } from '@dealstack/frontend/shared/components/BasicMantineTable/BasicMantineTable';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { InviteTeamMemberDrawer } from '@dealstack/frontend/shared/components/InviteTeamMemberDrawer/InviteTeamMemberDrawer';
import { StyledSettingsContainer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/styles';
import {
	useTranslationMipCreate,
	useTranslationOrganization,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	getResourceTypePermissions,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions';
import { DefaultMemberScope } from '@dealstack/frontend/app/pages/Organization/OrganizationDefaultMembers/OrganizationDefaultMembers.types';
import { useOrganizationDefaultMembers } from '@dealstack/frontend/app/pages/Organization/OrganizationDefaultMembers/useOrganizationDefaultMembers';
import { buildDefaultMemberColumns } from '@dealstack/frontend/app/pages/Organization/OrganizationDefaultMembers/organizationDefaultMembers.utils';

interface Props {
	organizationId: string;
}

export const ProgrammeDefaultsMembers = ({ organizationId }: Props) => {
	const tOrganization = useTranslationOrganization();
	// Programme roles, so the labels come from the same namespace the programme members page uses.
	const tMipCreate = useTranslationMipCreate();
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);

	const { members, isLoading, addMember, isAdding, removeMember, isRemoving } =
		useOrganizationDefaultMembers(organizationId, DefaultMemberScope.PROGRAMME);

	// Every programme role is offerable — a default member can be anything a real member can be.
	const permissions = useMemo(
		() =>
			getResourceTypePermissions(ResourceType.PROGRAMME).map((role) => ({
				label: tMipCreate(`invite_team_member.relation.${role}`),
				value: role,
			})),
		[tMipCreate]
	);

	const columns = buildDefaultMemberColumns({
		labels: {
			name: tOrganization('programme_defaults.members.table.name'),
			search: tOrganization('programme_defaults.members.table.search'),
			role: tOrganization('programme_defaults.members.table.role'),
			remove: tOrganization('programme_defaults.members.remove'),
		},
		roleLabel: (role) => tMipCreate(`invite_team_member.relation.${role}`),
		onRemove: removeMember,
		isRemoving,
	});

	return (
		<StyledSettingsContainer>
			<Flex direction="column" w="100%" gap="1rem">
				<Flex justify="space-between" align="center">
					<Text size="B6Medium" c="gray.9">
						{tOrganization('programme_defaults.members.description')}
					</Text>
					<Button
						leftSection={<Icon variant="add" size="lg" />}
						onClick={() => setIsDrawerOpen(true)}
					>
						{tOrganization('programme_defaults.members.add')}
					</Button>
				</Flex>

				<BasicMantineTable columns={columns} data={members} isLoading={isLoading} />
			</Flex>

			<InviteTeamMemberDrawer
				isOpen={isDrawerOpen}
				setIsOpen={setIsDrawerOpen}
				isLoading={isAdding}
				permissions={permissions}
				onInvite={addMember}
				description={tOrganization('programme_defaults.members.drawer.description')}
				roleLabel={tOrganization('programme_defaults.members.drawer.role_label')}
				submitLabel={tOrganization('programme_defaults.members.drawer.submit')}
				submitAndAddAnotherLabel={tOrganization(
					'programme_defaults.members.drawer.submit_and_add_another'
				)}
			/>
		</StyledSettingsContainer>
	);
};
